import '@hotwired/turbo-rails'
import appsignal from './appsignal'

// Core page navigation tracking
document.addEventListener('turbo:load', (event) => {
  appsignal.addBreadcrumb({
    category: 'navigation',
    action: 'turbo:load',
    metadata: {
      url: urlPathOnly(window.location.href),
      referrer: urlPathOnly(document.referrer),
      title: document.title,
      renderMethod: event.detail?.renderMethod || 'default',
    },
  })
})

// Track frame loads
document.addEventListener('turbo:frame-load', (event) => {
  const frame = event.target
  appsignal.addBreadcrumb({
    category: 'navigation',
    action: 'turbo:frame-load',
    metadata: {
      frameId: frame.id || 'unnamed-frame',
      frameSrc: frame.src || 'unknown',
      currentUrl: urlPathOnly(window.location.href),
    },
  })
})

// Track network responses
document.addEventListener('turbo:before-fetch-response', (event) => {
  // Turbo Drive does not send a referrer like Turbolinks used to, so let's simulate it here
  if (event.detail && event.detail.fetchOptions && event.detail.fetchOptions.headers) {
    event.detail.fetchOptions.headers['Turbo-Referrer'] = window.location.href
  }

  const { fetchResponse } = event.detail || {}

  appsignal.addBreadcrumb({
    category: 'network',
    action: 'turbo:before-fetch-response',
    metadata: {
      url: urlPathOnly(fetchResponse?.url),
      status: fetchResponse?.status || 0,
      statusText: fetchResponse?.statusText || '',
      contentType: fetchResponse?.contentType || 'unknown',
      redirected: fetchResponse?.redirected || false,
    },
  })
})

// Normalize dynamic frame IDs (e.g. "card_publication_45" → "card_publication_*")
// so AppSignal groups them per frame type, not per record.
function normalizeFrameId(frameId) {
  return frameId.replace(/_\d+$/, '_*')
}

// Strip query string and hash from a URL — search pages can carry
// user-entered queries (e.g. /publications?query=person-name) that
// should not land in telemetry. Returns the pathname only.
function urlPathOnly(url) {
  if (!url) return 'unknown'
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return typeof url === 'string' ? url.split('?')[0].split('#')[0] : 'unknown'
  }
}

function describeElement(el) {
  if (!el || !el.tagName) return 'unknown'
  const id = el.id ? `#${el.id}` : ''
  // Use classList (not className) — SVG elements expose className as
  // SVGAnimatedString rather than a plain string.
  const cls = Array.from(el.classList || [])
    .slice(0, 2)
    .map((c) => `.${c}`)
    .join('')
  const testid = el.getAttribute?.('data-testid')
  return `${el.tagName.toLowerCase()}${id}${cls}${testid ? `[data-testid="${testid}"]` : ''}`
}

// Record each turbo:click as an AppSignal breadcrumb. AppSignal auto-
// attaches the recent breadcrumb trail to any error sent later in the
// session, so the turbo:frame-missing handler does not need to track
// click state itself — it just sends the error and the click history
// rides along. Mirrors the pattern used by @appsignal/plugin-breadcrumbs-
// network for XHR/fetch events.
document.addEventListener('turbo:click', (event) => {
  const link = event.target
  appsignal.addBreadcrumb({
    category: 'navigation',
    action: 'turbo:click',
    metadata: {
      href: urlPathOnly(event.detail?.url || link?.href),
      turboFrame:
        link?.getAttribute?.('data-turbo-frame') || link?.closest?.('turbo-frame')?.id || 'none',
      selector: describeElement(link),
    },
  })
})

// Track missing frames - the critical error case
document.addEventListener('turbo:frame-missing', async (event) => {
  const targetFrame = event.target
  // Turbo dispatches detail.response as the RAW native Response
  // (see @hotwired/turbo 8.0.23 line 6721: `const response = fetchResponse.response`),
  // not the FetchResponse wrapper. Use native Response APIs: .url, .status,
  // .headers.get(), .clone().text().
  const response = event.detail?.response
  const responseUrl = response?.url || 'unknown'
  const responseStatus = response?.status || 'unknown'
  const frameId = targetFrame?.id || 'unnamed-frame'
  const frameSrc = targetFrame?.src || 'unknown'
  const requestId = response?.headers?.get('X-Request-Id') || 'unknown'
  const normalizedFrameId = normalizeFrameId(frameId)

  // Enumerate <turbo-frame id="..."> IDs present in the response body.
  // Only attempt on HTML responses; a JSON rescue or a 304 with empty
  // body lands in 'non-html'. 'read-error' means the body was already
  // consumed by Turbo before we got here.
  let responseFrames = 'unknown'
  try {
    const contentType = response?.headers?.get('Content-Type') || ''
    if (contentType.includes('text/html') || contentType.includes('application/xhtml+xml')) {
      const html = await response.clone().text()
      const ids = Array.from(html.matchAll(/<turbo-frame[^>]+id="([^"]+)"/g), (m) => m[1])
      responseFrames = ids.join(',') || 'none'
    } else {
      responseFrames = contentType ? 'non-html' : 'no-response'
    }
  } catch (e) {
    responseFrames = `read-error:${e?.name || 'unknown'}`
  }

  const errorMessage = `Frame missing: Expected frame "${frameId}" in response from "${responseUrl}" (status: ${responseStatus})`

  // Use a custom error name per frame type so AppSignal creates separate incidents
  const error = new Error(errorMessage)
  error.name = `TurboFrameMissing[${normalizedFrameId}]`

  appsignal.sendError(error, (span) => {
    span.setAction(`turbo:frame-missing#${normalizedFrameId}`)
    span.setTags({
      frameId,
      normalizedFrameId,
      frameSrc,
      responseUrl,
      responseStatus: String(responseStatus),
      responseFrames,
      requestId,
      // pathname (not href) to keep query strings out of telemetry.
      currentPage: window.location.pathname,
    })
  })
})

// Track form submissions - start and end only
document.addEventListener('turbo:submit-start', (event) => {
  const form = event.target
  appsignal.addBreadcrumb({
    category: 'interaction',
    action: 'turbo:submit-start',
    metadata: {
      formId: form?.id || 'unnamed-form',
      formAction: urlPathOnly(form?.action) || 'unknown',
      formMethod: form?.method || 'unknown',
      currentUrl: urlPathOnly(window.location.href),
    },
  })
})

document.addEventListener('turbo:submit-end', (event) => {
  const form = event.target
  appsignal.addBreadcrumb({
    category: 'interaction',
    action: 'turbo:submit-end',
    metadata: {
      formId: form?.id || 'unnamed-form',
      formAction: urlPathOnly(form?.action) || 'unknown',
      success: event.detail?.success || false,
      status: event.detail?.fetchResponse?.status || 'unknown',
    },
  })
})
